import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "./richTextArea.scss";

/**
 * A small rich-text field — the progress-notes composer's editor pattern
 * (contentEditable + execCommand), packaged as a reusable control. Toolbar:
 * B / I / U / S and bulleted/numbered lists.
 *
 * UNCONTROLLED on purpose: React cannot drive a contentEditable through a
 * `value` prop without fighting the caret. `defaultHtml` is written into the
 * DOM exactly once on mount; after that the browser owns the subtree and
 * `onChange(innerHTML)` reports edits upward.
 *
 * SECURITY: the CALLER must pass `defaultHtml` already sanitised (DOMPurify) —
 * seeding raw wire HTML into a live DOM fires onerror/onload payloads even
 * without <script>. This component deliberately does not sanitise, so it can't
 * be mistaken for the safety boundary.
 */
const RichTextArea = ({
  defaultHtml,
  onChange,
  placeholder,
  ariaLabel,
  className,
  styleControls,
  plain,
  disabled,
}) => {
  const ref = useRef(null);
  // The HTML last reported (or seeded). Blur and no-op commands re-emit the
  // same markup, and reporting that as a change marks a clean form dirty.
  const lastHtml = useRef("");

  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = defaultHtml || "";
      // Read back from the DOM: the browser normalises the seeded markup.
      lastHtml.current = ref.current.innerHTML;
    }
    // Mount-only by design (uncontrolled) — see the header comment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el || !onChange) return;
    const html = el.innerHTML;
    if (html === lastHtml.current) return;
    lastHtml.current = html;
    onChange(html);
  }, [onChange]);

  // styleWithCSS=false keeps output as semantic tags (<b>/<i>…), which the
  // sanitiser's allow-list keeps — style-span output would be stripped on save.
  const exec = (command, value) => {
    if (disabled) return;
    if (ref.current) ref.current.focus();
    try {
      document.execCommand("styleWithCSS", false, false);
    } catch (e) {
      /* engines without the toggle still run the command */
    }
    document.execCommand(command, false, value);
    emit();
  };

  /**
   * Text size, as a span carrying a px `font-size`.
   *
   * execCommand("fontSize") only speaks HTML's 1-7 scale and emits
   * `<font size="n">`, which is neither a px value nor something the note's
   * sanitiser or the patient app's renderer will keep. So: run it with the
   * sentinel 7, then swap each `<font size="7">` it just produced for a span
   * with the real size. That keeps the browser's own selection handling -
   * partial selections, several blocks - and changes only the markup.
   */
  const applySize = (px) => {
    if (disabled) return;
    const host = ref.current;
    if (!host) return;
    host.focus();
    try {
      document.execCommand("styleWithCSS", false, false);
    } catch (e) {
      /* engines without the toggle still run the command */
    }
    document.execCommand("fontSize", false, "7");
    Array.from(host.querySelectorAll('font[size="7"]')).forEach((font) => {
      const span = document.createElement("span");
      span.style.fontSize = `${px}px`;
      while (font.firstChild) span.appendChild(font.firstChild);
      if (font.parentNode) font.parentNode.replaceChild(span, font);
    });
    emit();
  };

  /* The block-style and size menus, shown only where the field's consumer also
     KEEPS this markup: sanitizeRichText drops headings and spans unless asked
     for them, so offering these to a consumer that has not opted in would be a
     toolbar whose choices vanish on save. See richText.js. */
  const BLOCKS = [
    { value: "p", label: "Normal" },
    { value: "h1", label: "Heading 1" },
    { value: "h2", label: "Heading 2" },
    { value: "h3", label: "Heading 3" },
  ];
  const SIZES = [12, 14, 16, 18, 20, 24];

  const tools = [
    { cmd: "bold", label: "B", cls: "rt-b", name: "Bold" },
    { cmd: "italic", label: "I", cls: "rt-i", name: "Italic" },
    { cmd: "underline", label: "U", cls: "rt-u", name: "Underline" },
    { cmd: "strikeThrough", label: "S", cls: "rt-s", name: "Strikethrough" },
    { cmd: "insertUnorderedList", label: "•≡", cls: "rt-ul", name: "Bulleted list" },
    { cmd: "insertOrderedList", label: "1≡", cls: "rt-ol", name: "Numbered list" },
  ];

  /* PLAIN: text and line breaks only. Dropping the toolbar is not enough on its
     own — a contentEditable still bolds on Ctrl/Cmd+B, I and U, and a paste
     brings the source's markup with it. */
  const blockFormatKeys = (e) => {
    if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && /^[biu]$/i.test(e.key)) {
      e.preventDefault();
    }
  };
  // Each pasted line becomes its own block, exactly as if Enter had been typed.
  const pasteAsText = (e) => {
    const data = e.clipboardData;
    if (disabled || !data) return;
    e.preventDefault();
    const text = (data.getData("text/plain") || "").replace(/\r\n?/g, "\n");
    text.split("\n").forEach((line, i) => {
      if (i) document.execCommand("insertParagraph");
      if (line) document.execCommand("insertText", false, line);
    });
    emit();
  };

  return (
    <div className={`rt-area${className ? ` ${className}` : ""}${disabled ? " is-disabled" : ""}`}>
      {plain ? null : (
        <div className="rt-toolbar">
          {styleControls ? (
            <>
              <select
                className="rt-select rt-block"
                title="Text style"
                aria-label="Text style"
                disabled={disabled}
                defaultValue="p"
                onChange={(e) => exec("formatBlock", `<${e.target.value}>`)}
              >
                {BLOCKS.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
              <select
                className="rt-select rt-size"
                title="Text size"
                aria-label="Text size"
                disabled={disabled}
                defaultValue={14}
                onChange={(e) => applySize(Number(e.target.value))}
              >
                {SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </>
          ) : null}
          {tools.map((t) => (
            <button
              key={t.cmd}
              type="button"
              className={`rt-btn ${t.cls}`}
              title={t.name}
              aria-label={t.name}
              disabled={disabled}
              // Keep the editor's selection so the command hits highlighted text.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => exec(t.cmd)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <div
        ref={ref}
        className="rt-editor"
        // Disabled keeps the editor MOUNTED (its uncontrolled content and
        // layout stay put) but takes typing and the toolbar away.
        contentEditable={!disabled}
        aria-disabled={disabled || undefined}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        data-placeholder={placeholder || ""}
        onInput={emit}
        onBlur={emit}
        onKeyDown={plain ? blockFormatKeys : undefined}
        onPaste={plain ? pasteAsText : undefined}
      />
    </div>
  );
};

RichTextArea.propTypes = {
  /** Initial HTML — MUST already be sanitised by the caller. */
  defaultHtml: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  /** Extra class on the root, so a host can size/theme the field. */
  className: PropTypes.string,
  /**
   * Show the block-style and text-size menus.
   *
   * OFF by default, and deliberately: what they emit (h1-h3, a span with a
   * font-size) only survives `sanitizeRichText(html, { headings, fontSize })`,
   * so a host that has not opted in there must not offer them here either.
   */
  styleControls: PropTypes.bool,
  /**
   * Plain text only: no toolbar, the Ctrl/Cmd+B/I/U shortcuts do nothing, and a
   * paste lands as its text. Lines still come out as separate blocks.
   */
  plain: PropTypes.bool,
  /** Inert for now (e.g. while the host saves or regenerates): no typing, no toolbar. */
  disabled: PropTypes.bool,
};

export default RichTextArea;
